import {
  type Granularity,
  type Installation,
  type RevenueEntry,
  type Subscription,
  subscriptionContributesMrrAt,
} from "@seed-factory/core";
import { appConfig } from "./config";
import {
  civilBucketLabel,
  civilDateValue,
  civilMonthStart,
  civilWeekStart,
  dateForCivilDay,
  shiftCivilDays,
  shiftCivilMonths,
} from "./dates";

export type SeriesPoint = {
  label: string;
  installs: number;
  activations: number;
  firstValue: number;
  revenue: number;
  mrr: number;
  churn: number;
};

function bucketKey(date: Date, granularity: Granularity, timeZone: string): string {
  const civil = civilDateValue(date, timeZone);
  if (granularity === "month") return civilMonthStart(civil);
  if (granularity === "week") return civilWeekStart(civil);
  return civil;
}

function nextBucket(value: string, granularity: Granularity): string {
  if (granularity === "month") return shiftCivilMonths(value, 1);
  if (granularity === "week") return shiftCivilDays(value, 7);
  return shiftCivilDays(value, 1);
}

function bucketKeys(from: Date, to: Date, granularity: Granularity, timeZone: string): string[] {
  const endCivil = civilDateValue(to, timeZone);
  const keys: string[] = [];
  let current = bucketKey(from, granularity, timeZone);
  while (current <= endCivil) {
    keys.push(current);
    current = nextBucket(current, granularity);
  }
  return keys;
}

export function buildSeries(input: {
  installations: Installation[];
  subscriptions: Subscription[];
  revenue: RevenueEntry[];
  from: Date;
  to: Date;
  granularity: Granularity;
  timeZone?: string;
}): SeriesPoint[] {
  const timeZone = input.timeZone ?? appConfig.timezone;
  const keys = bucketKeys(input.from, input.to, input.granularity, timeZone);
  const map = new Map<string, SeriesPoint>();
  for (const key of keys) {
    map.set(key, {
      label: civilBucketLabel(key, input.granularity),
      installs: 0,
      activations: 0,
      firstValue: 0,
      revenue: 0,
      mrr: 0,
      churn: 0,
    });
  }

  const add = (value: string | null, field: keyof Omit<SeriesPoint, "label">, amount = 1) => {
    if (!value) return;
    const date = new Date(value);
    if (date < input.from || date > input.to) return;
    const point = map.get(bucketKey(date, input.granularity, timeZone));
    if (point) point[field] += amount;
  };

  for (const row of input.installations) {
    add(row.installedAt, "installs");
    add(row.activatedAt, "activations");
    add(row.firstValueAt, "firstValue");
  }
  for (const row of input.revenue) add(row.recognizedAt, "revenue", row.amountCents / 100);
  for (const row of input.subscriptions) add(row.canceledAt, "churn");

  return keys.map((key, index) => {
    const point = map.get(key);
    if (!point) throw new Error("SERIES_BUCKET_MISSING");
    const nextKey = keys[index + 1];
    const end = nextKey
      ? new Date(dateForCivilDay(nextKey, false, timeZone).getTime() - 1)
      : input.to;
    point.mrr = input.subscriptions
      .filter((row) => subscriptionContributesMrrAt(row, end))
      .reduce((sum, row) => sum + row.mrrCents / 100, 0);
    return point;
  });
}
